import { flow, Instance, getEnv, types, cast } from "mobx-state-tree";

import { UserOrganizationResponse } from "../../api-models/response/user/UserResponse";
import { DataGridStorageModel } from "../../components/datatable/DataGridStorage";
import { Column, ColumnType } from "../../components/datatable/interfaces";
import { endpoints } from "../api/ApiEndpoints";
import { UserOrganization } from "../user/my-account/UserOrganization";
import { Option } from "../../components/datatable/interfaces";
import { organizationSubscriptionService } from "./OrganizationSubscription";
import { OrganizationStatusResponse } from "../../api-models/response/organization/OrganizationDetailsResponse";
import { EmploymentStatusResponse } from "../../api-models/response/EmploymentStatusResponse";
import { Role } from "../user/Role";

const MyOrganizationsModel = types.model({
  organizationStatusOptions: types.array(types.frozen<Option>()),
  userRoleOptions: types.array(types.frozen<Option>()),
  employmentStatusOptions: types.array(types.frozen<Option>()),
});

export const MyOrganizationsStorageModel = types
  .compose(DataGridStorageModel, MyOrganizationsModel)
  .named("MyOrganizationsStorage")
  .views((self) => ({
    get tableColumns(): Column[] {
      return [
        {
          key: "name",
          name: "organization.DisplayOrganizationName",
          type: ColumnType.String,
          sortable: true,
          filterable: true,
          flex: 2,
        },
        {
          key: "status",
          name: "organization.DisplayOrganizationStatus",
          type: ColumnType.SingleSelect,
          sortable: true,
          filterable: true,
          valueOptions: self.organizationStatusOptions,
        },
        {
          key: "contact",
          name: "organization.DisplayOrganizationContactName",
          type: ColumnType.String,
          sortable: true,
          filterable: true,
        },
        {
          key: "email",
          name: "organization.DisplayOrganizationEmail",
          type: ColumnType.String,
          sortable: true,
          filterable: true,
        },
        {
          key: "phone",
          name: "organization.organization-phone",
          type: ColumnType.String,
          sortable: true,
          filterable: true,
        },
        {
          key: "employmentStatus",
          name: "organization.DisplayOrganizationEmploymentStatus",
          type: ColumnType.SingleSelect,
          sortable: true,
          filterable: true,
          valueOptions: self.employmentStatusOptions,
        },
        {
          key: "role",
          name: "organization.DisplayOrganizationEmploymentRole",
          type: ColumnType.SingleSelect,
          sortable: true,
          filterable: true,
          valueOptions: self.userRoleOptions,
        },
      ];
    },
  }))
  .actions((self) => {
    return {
      mapItem(item: UserOrganizationResponse): UserOrganization {
        self.gridColumns = self.tableColumns;
        return new UserOrganization(item);
      },
      setOrganizationStatusOptions(organizationStatuses: OrganizationStatusResponse[]) {
        const organizationStatusesOptions = organizationStatuses.map((organizationStatus) => {
          return {
            label: organizationStatus.translationKey,
            value: organizationStatus.id.toString(),
          };
        });
        self.organizationStatusOptions = cast(organizationStatusesOptions);
      },
      setUserRoleOptions(userRoles: Role[]) {
        const userRolesOptions = userRoles.map((role) => {
          return {
            label: role.translationKey,
            value: role.id.toString(),
          };
        });
        self.userRoleOptions = cast(userRolesOptions);
      },
      setEmploymentStatusOptions(employmentStatuses: EmploymentStatusResponse[]) {
        const employmentStatusesOptions = employmentStatuses.map(
          (employmentStatus: EmploymentStatusResponse) => {
            return {
              label: `general.user.employment-status.${employmentStatus.name}`,
              value: employmentStatus.id.toString(),
            };
          }
        );
        self.employmentStatusOptions = cast(employmentStatusesOptions);
      },
      stopUserCollaboration: flow(function* stopUserCollaboration(id: number) {
        const endpoint = endpoints.get("remove_from_organization");
        endpoint.routeParams = [id];
        const response = yield getEnv(self).api.delete(endpoint);
        return response;
      }),
      requestDeleteOrganization: flow(function* (organizationId: number) {
        const endpoint = endpoints.get("request_delete_organization");
        endpoint.routeParams = [organizationId];
        yield getEnv(self).api.post(endpoint);
        organizationSubscriptionService.removeOrganiztionSubscriptionKey();
      }),
      afterCreate() {
        self.defaultSort = { field: "status", sort: "asc" };
      },
    };
  })
  .views((self) => ({
    get endpoint() {
      const endpoint = endpoints.get("user_organizations");
      endpoint.queryParams = self.getDataQuery();
      return endpoint;
    },
    get selectedItem(): UserOrganization | undefined {
      if (self.selectedItems.length <= 0 || !self.selectedItems[0]) return undefined;

      return self.data?.items.find(
        (e: UserOrganization) => e.id.toString() === self.selectedItems[0]
      );
    },
  }))
  .views((self) => ({
    get idOfSelectedItem(): number {
      return self.selectedItem?.id ?? 0;
    },
    get roleIdOfSelectedItem(): number {
      return self.selectedItem?.roleId ?? 0;
    },
    get nameOfSelectedItem(): string {
      return self.selectedItem?.name ?? "";
    },
    get lockedInspectionsCountOfSelectedItem(): number {
      return self.selectedItem?.lockedInspectionsCount ?? 0;
    },
    get hasPaidSubscriptionOfSelectedItem(): boolean {
      return self.selectedItem?.hasPaidSubscription ?? true;
    },
    get organizationsWithSubscriptionsCanceled(): [] {
      return self.data?.items.filter((x: UserOrganization) => x.subscriptionCancelationDate);
    },
  }));

export type MyOrganizationsStorage = Instance<typeof MyOrganizationsStorageModel>;
